import { fetchFormHookLogs } from "@/api/forms";
import { formatApiQueryError } from "@/lib/http/formatQueryError";
import type { PaginatedMeta } from "@/types/formList";
import type { HookLogDto } from "@/types/hookLog";
import { useAsyncResource } from "./useAsyncResource";

export const HOOK_LOGS_PER_PAGE_OPTIONS = [15, 25, 50] as const;

interface HookLogsPage {
  logs: HookLogDto[];
  meta: PaginatedMeta | null;
}

interface UseFormHookLogsQueryOptions {
  formId: number;
  page: number;
  perPage: number;
  filterSubmissionId?: number | null;
  enabled?: boolean;
}

interface UseFormHookLogsQueryResult {
  logs: HookLogDto[];
  meta: PaginatedMeta | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

export function useFormHookLogsQuery({
  formId,
  page,
  perPage,
  filterSubmissionId = null,
  enabled = true,
}: UseFormHookLogsQueryOptions): UseFormHookLogsQueryResult {
  const { data, isLoading, error, reload } = useAsyncResource<HookLogsPage>({
    enabled,
    deps: [formId, page, perPage, filterSubmissionId],
    fetch: () =>
      fetchFormHookLogs(formId, {
        page,
        count: perPage,
        submissionId: filterSubmissionId ?? undefined,
      }).then((response) => ({
        logs: response.data ?? [],
        meta: response.meta ?? null,
      })),
    formatError: (err) =>
      formatApiQueryError(err, "Не удалось загрузить журнал вебхуков", {
        forbidden: "Нет доступа к журналу вебхуков.",
        statusPrefix: "Не удалось загрузить журнал вебхуков",
      }),
    initialLoading: false,
  });

  return {
    logs: data?.logs ?? [],
    meta: data?.meta ?? null,
    isLoading,
    error,
    reload,
  };
}

export function hookLogRowKey(log: HookLogDto): string {
  return `${log.submissionId}-${log.id}-${log.attempt}`;
}
